import { Grid, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import KenAccordion from '../../Components/KenAccordion';
import KenLoader from '../../Components/KenLoader';
import { AuthContext } from '../../Context/authContext';
import { getDocuments, createDocuments } from '../../Utils/apiServices';
import DocumentList from './Components/DocumentList';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@mui/styles';
import { TABLET_BREAKPOINT } from '../../Constants/constant';

const useStyles = makeStyles((theme) => ({
  title: {
    'font-style': 'normal',
    'font-weight': '500',
    'font-size': '18px',
    'line-height': '100%',
    'letter-spacing': '-0.02em',
    'text-transform': 'uppercase',
    color: theme.palette.KenColors.dark,
    display: 'flex',
    position: 'relative',

    '& > .underline': {
      position: 'absolute',
      width: '55px',
      height: '4px',
      top: '24px',
      background: theme.palette.KenColors.statusYellow,
    },
  },
  description: {
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      background: '#F7F8FB',
      width: '100%',
      padding: '27px 27px 10px 27px !important',
    },
  },
  documentTitle: {
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      'font-style': 'normal',
      'font-weight': '500',
      'font-size': '22px',
      'line-height': '100%',
      color: theme.palette.KenColors.neutral900,
    },
  },
  documentSubTitle: {
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      'font-style': 'normal',
      'font-weight': '400',
      'font-size': '14px',
      'line-height': '150%',
      color: theme.palette.KenColors.neutral900,
    },
  },
}));

export default function DocumentPage() {
  const [uploadData, setUploadData] = useState();
  const [loader, setLoader] = useState(false);
  const { t } = useTranslation();
  const {
    state: { user },
  } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(
    theme.breakpoints.down(TABLET_BREAKPOINT)
  );
  const [trigger, setTrigger] = useState(0);

  useEffect(() => {
    setLoader(true);
    getDocuments(user?.applicantId)
      .then((res) => {
        setLoader(false);
        if (res?.success) {
          const customData = [...res?.data];
          customData.forEach((items) => {
            // Destructuring data from applicationchecklist
            if (items.applicationCheckList.length > 0) {
              items.applicationCheckList.forEach((subItems) => {
                items.documents.push({
                  ...subItems,
                  title: subItems?.documentType,
                  fileName: subItems?.submittedFileName,
                  status: subItems?.documentStatus,
                });
              });
            }

            // Initializing applicationID
            items?.documents?.forEach((customItems) => {
              customItems['applicationId'] = items.applicationId;
              return { ...customItems };
            });
          });
          setUploadData(customData);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoader(false);
      });
  }, [user?.applicantId, trigger]);

  const handleFileChange = (file, elem) => {
    setLoader(true);
    const reader = new FileReader();
    let base64;
    reader.onloadend = () => {
      base64 = reader.result.replace('data:', '').replace(/^.+,/, '');
      const myArray = file.name.split('.');
      const body = {
        fileName: `${user?.applicantId}_${file.name}`,
        title: elem.title,
        formattedDocumentName: `${elem?.applicationProgramName}_${
          elem?.applicationName
        }_${user?.applicantName}.${myArray[myArray.length - 1]}`,
        expiryDate: null,
        body: base64,
        applicationId: elem.applicationId,
        fileType: file.type,
        recordId: elem?.recordId ? elem?.recordId : null
      };
      createDocuments({ attachments: [body] })
        .then((res) => {
          if (res?.success === true) {
            setLoader(false);
            setTrigger(trigger + 1);
            return enqueueSnackbar('Document Uploaded Successfully', {
              variant: 'success',
            });
          }
        })
        .catch((err) => {
          setLoader(false);
          return enqueueSnackbar(
            'Error while uploading. Please try again later.',
            {
              variant: 'error',
            }
          );
        });
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      {loader && <KenLoader />}
      <Box
        sx={{
          m: isMobileScreen ? 0 : 2,
          width: '90%',
        }}
      >
        <Grid container direction="column" className={classes.description}>
          <Grid item mb={2}>
            <Typography
              variant="body8"
              sx={{ float: 'left' }}
              className={classes.title}
            >
              {t('headings:My_Documents')}
              <span className="underline"></span>
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body1"
              sx={{ textAlign: 'left', mb: 2 }}
              className={classes.documentSubTitle}
            >
              {t('messages:My_document_description')}
            </Typography>
          </Grid>
        </Grid>
        {uploadData &&
          uploadData.map((el) => {
            return (
              <KenAccordion label={`${el?.applicationProgramName} Documents`}>
                {el?.documents.length > 0 ? (
                  el?.documents.map((elem, index) => {
                    return (
                      <Box mb={2}>
                        <DocumentList
                          {...elem}
                          onChange={(files) => {
                            handleFileChange(files[0], elem);
                          }}
                          title={`${index + 1}. ${elem?.title}`}
                          fileName={elem?.fileName}
                          status={elem?.status}
                        />
                      </Box>
                    );
                  })
                ) : (
                  <Typography>No Documents Required</Typography>
                )}
              </KenAccordion>
            );
          })}
      </Box>
    </>
  );
}
